import { LoginCredentials, SignupCredentials, AuthResponse, AccountRequest, AccountRequestResponse, User } from '../types/auth';
import { Portfolio, PortfolioDetail, CreatePortfolioRequest, UpdatePortfolioRequest, PortfolioListResponse, PortfolioResponse, CreatePortfolioResponse, InvitePartnerRequest, InvitePartnerResponse, RemovePartnerResponse } from '../types/portfolio';
import { Property, PropertyDetail, CreatePropertyRequest, UpdatePropertyRequest, PropertyListResponse, PropertyResponse, CreatePropertyResponse, AddPropertyPartnerRequest, UpdatePropertyPartnerRequest, PropertyPartnerResponse, PortfolioView } from '../types/property';
import {
  Opportunity,
  OpportunityDetail,
  OpportunityStatus,
  OpportunityView,
  CreateOpportunityRequest,
  UpdateOpportunityRequest,
  OpportunityListResponse,
  OpportunityResponse,
  CreateOpportunityResponse,
  AddOpportunityPartnerRequest,
  UpdateOpportunityPartnerRequest,
  OpportunityPartnerResponse,
  Parcel,
} from '../types/opportunity';
import { FinancialEntry, CreateFinancialEntryRequest, UpdateFinancialEntryRequest, FinancialEntryListResponse, FinancialEntryResponse, PortfolioFinancialSummaryResponse } from '../types/financial';
import { MortgageCalculation, CreateMortgageCalculationRequest, MortgageCalculationResponse, MortgageCalculationListResponse } from '../types/mortgage';
// Old due diligence types - kept for backward compatibility with existing API functions
// These may be defined in server-side code or legacy types
type DueDiligenceChecklist = any;
type DueDiligenceChecklistItem = any;
type DueDiligenceItemNote = any;
type CreateChecklistItemRequest = any;
type UpdateChecklistItemRequest = any;
type CreateItemNoteRequest = any;
type ChecklistResponse = any;
type ChecklistItemResponse = any;
type ItemNoteResponse = any;
type PropertyPotentialSurvey = any;
type PropertyPotentialSurveyItem = any;
type PropertyPotentialSurveyVote = any;
type CreateSurveyRequest = any;
type CreateSurveyItemRequest = any;
type SurveyResponse = any;
type SurveyListResponse = any;
type SurveyItemResponse = any;

// V2 types - new due diligence system
import {
  DueDiligence,
  DueDiligenceItem,
  CreateDueDiligenceRequest,
  UpdateDueDiligenceRequest,
  UpdateDueDiligenceItemRequest,
  DueDiligenceResponse,
} from '../types/due-diligence';
import { Contact, ContactType, CreateContactRequest, UpdateContactRequest, ContactListResponse, ContactResponse, ContactTypesResponse } from '../types/contact';
import {
  CommentThread,
  Comment,
  CommentReaction,
  CreateCommentThreadRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  AddCommentReactionRequest,
  CommentThreadResponse,
  CommentThreadListResponse,
  CommentResponse,
  CommentListResponse,
  CommentReactionResponse,
  MarkThreadReadResponse,
} from '../types/comments';
import {
  IdeaWithDetails,
  IdeaVote,
  IdeaComment,
  IdeaActivity,
  CreateIdeaRequest,
  UpdateIdeaRequest,
  VoteIdeaRequest,
  CreateIdeaCommentRequest,
  IdeaListResponse,
  IdeaResponse,
  IdeaVoteResponse,
  IdeaCommentListResponse,
  IdeaCommentResponse,
  IdeaActivityListResponse,
  IdeaFilters,
} from '../types/idea';

// Geocode Response Types
export interface GeocodePropertyResponse {
  success: boolean;
  geocoded: {
    latitude: number;
    longitude: number;
    county: string | null;
    place_id: string | null;
    formatted_address: string;
    accuracy: string | null;
    location_type: string;
  };
  _mock?: boolean;
}

export interface AddressSearchResult {
  success: boolean;
  original_address: string;
  formatted_address: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  location_type: string;
  accuracy: string | null;
  address: {
    street_address: string | null;
    city: string | null;
    county: string | null;
    state: string | null;
    state_abbr: string | null;
    zip_code: string | null;
    country: string | null;
    country_abbr: string | null;
    neighborhood: string | null;
  };
  place_id: string;
  bbox: [number, number, number, number] | null;
  property_data: {
    latitude: number;
    longitude: number;
    street_address: string | null;
    city: string | null;
    state: string | null;
    zip_code: string | null;
    county: string | null;
    country: string | null;
    place_id: string;
  };
}

export interface AddressSearchResponse {
  success: boolean;
  results: AddressSearchResult[];
  query: string;
  error?: string;
}

// Data Lookup Types (legacy - using Mapbox now)
export interface GeocodeResult {
  success: boolean;
  error?: string;
  original_address?: string;
  formatted_address?: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  location_type?: string;
  address?: {
    street_address: string | null;
    city: string | null;
    county: string | null;
    state: string | null;
    state_abbr: string | null;
    zip_code: string | null;
    country: string | null;
    country_abbr: string | null;
  };
  place_id?: string;
  due_diligence_data?: {
    latitude?: number;
    longitude?: number;
    physical_address?: string | null;
    county?: string | null;
    municipality?: string | null;
    jurisdiction_notes?: string;
    has_911_address?: boolean;
  };
  _mock?: boolean;
}

export interface AddressValidationResult extends GeocodeResult {
  valid: boolean;
  accuracy?: string;
  is_precise?: boolean;
  suggested_address?: string;
}

export interface PropertyLookupResult {
  success: boolean;
  error?: string;
  sources: Array<{
    name: string;
    status: 'success' | 'error';
    data_provided?: string[];
    error?: string;
  }>;
  property_data: {
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip_code?: string | null;
    county?: string | null;
    latitude?: number;
    longitude?: number;
    formatted_address?: string;
  };
  due_diligence_data: {
    gps_coords?: { latitude: number; longitude: number };
    address_verify?: {
      physical_address?: string | null;
      mailing_address?: string;
      has_911_address?: boolean;
    };
    jurisdiction?: {
      county?: string | null;
      municipality?: string | null;
      jurisdiction_notes?: string;
    };
  };
  geocode?: GeocodeResult;
}

// Smart Contacts Types
export interface SmartContact extends Contact {
  source: 'property' | 'portfolio' | 'user';
  added_by: number;
  added_by_info: {
    email: string;
    display_name: string | null;
    isCurrentUser: boolean;
  };
}

export interface SmartContactsResponse {
  success: boolean;
  contacts: {
    property: SmartContact[];
    portfolio: SmartContact[];
    my_contacts: SmartContact[];
    partner_contacts: SmartContact[];
    all: SmartContact[];
  };
  by_type: Record<string, { count: number; contacts: SmartContact[] }>;
  suggested_types: string[];
  total: number;
  partners: Array<{
    user_id: number;
    email: string;
    display_name: string | null;
    is_current_user: boolean;
  }>;
}

// ==========================================================================
// Parcel / Geographic Feature Types
// ==========================================================================

import {
  GeoLayerType,
  GeoDataSource,
  GeoFeature,
  GeoFeatureSummary,
  GeoFeatureWithGeometry,
  PropertyGeoFeature,
  NeighborsByTier,
  GeoJSONGeometry,
  BoundingBox,
} from '../types/parcel';

export interface LayerTypesResponse {
  success: boolean;
  layer_types: GeoLayerType[];
}

export interface DataSourcesResponse {
  success: boolean;
  sources: GeoDataSource[];
}

export interface ParcelLookupParams {
  lat?: number;
  lng?: number;
  address?: string;
  parcel_id?: string;
  state?: string;
  county?: string;
  include_neighbors?: boolean;
  max_tier?: number;
  include_geometry?: boolean;
}

export interface ParcelLookupResponse {
  success: boolean;
  feature?: GeoFeature & { layer_code?: string; layer_name?: string };
  features?: Array<GeoFeature & { layer_code?: string; layer_name?: string }>;
  neighbors?: NeighborsByTier[];
  source?: string;
  message?: string;
  match_confidence?: number;
  match_count?: number;
}

export interface FeatureResponse {
  success: boolean;
  feature?: GeoFeature & { layer_code?: string; layer_name?: string };
}

export interface NeighborsResponse {
  success: boolean;
  feature_id: number;
  neighbors_by_tier: NeighborsByTier[];
  total_neighbors: number;
}

export interface PropertyFeaturesResponse {
  success: boolean;
  property_id: number;
  features: Array<PropertyGeoFeature & GeoFeatureWithGeometry & {
    layer_code?: string;
    layer_name?: string;
    layer_category?: string;
    default_color?: string;
    default_opacity?: number;
  }>;
  neighborsByTier?: NeighborsByTier[];
  neighborSource?: 'graph' | 'api_cache' | null;
}

export interface LinkParcelParams {
  feature_id?: number;
  auto_link?: boolean;
  association_type?: string;
  include_neighbors?: boolean;
  max_tier?: number;
  notes?: string;
}

export interface LinkParcelResponse {
  success: boolean;
  message?: string;
  link?: PropertyGeoFeature;
  feature?: GeoFeature;
  primaryParcel?: GeoFeature;
  neighborCount?: number;
  source?: string;
}

export interface ParcelSearchParams {
  owner?: string;
  address?: string;
  county?: string;
  state?: string;
  min_acres?: number;
  max_acres?: number;
  limit?: number;
}

// Opportunity Photo types
export interface OpportunityPhoto {
  id: number;
  opportunity_id: number;
  uploaded_by: number;
  filename: string;
  mime_type: string;
  caption: string | null;
  thumbnail_data: string | null;
  latitude: number | null;
  longitude: number | null;
  taken_at: string | null;
  created_at: string;
  updated_at: string;
  uploader_name: string | null;
}

export interface VoiceNote {
  id: number;
  opportunity_id: number;
  recorded_by: number;
  filename: string;
  mime_type: string;
  duration_ms: number | null;
  transcript: string | null;
  caption: string | null;
  latitude: number | null;
  longitude: number | null;
  recorded_at: string;
  created_at: string;
  updated_at: string;
  recorder_name: string | null;
}

export const FIELD_NOTE_CATEGORIES = [
  'observation', 'condition', 'boundary', 'access',
  'water', 'timber', 'soil', 'wildlife', 'other',
] as const;

export type FieldNoteCategory = typeof FIELD_NOTE_CATEGORIES[number];

export interface FieldNote {
  id: number;
  opportunity_id: number;
  created_by: number;
  category: FieldNoteCategory;
  title: string | null;
  body: string;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
  updated_at: string;
  author_name: string | null;
}

// Activity Log types
export interface ActivityLogEntry {
  id: number;
  action: string;
  entity_type: string;
  entity_id: number;
  entity_name?: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  metadata?: Record<string, any>;
  created_at: string;
  user?: {
    id: number;
    email: string;
    display_name?: string;
  };
}

export interface ActivityLogResponse {
  success: boolean;
  activities: ActivityLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface ParcelSearchResponse {
  success: boolean;
  features: GeoFeatureSummary[];
  total_count: number;
  search_params: {
    owner?: string;
    address?: string;
    county?: string;
    state?: string;
  };
}

import {
  Feedback,
  CreateFeedbackRequest,
  FeedbackResponse,
  FeedbackListResponse,
  FeedbackStatsResponse,
} from '../types/feedback';
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import { logger } from './logger';

// API URL with production fallback for web builds
const API_URL = process.env.EXPO_PUBLIC_API_URL || (typeof window !== 'undefined' && window.location.hostname !== 'localhost' ? 'https://api.farmerjohn.pacayagroup.com' : 'http://localhost:3001');

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Get token from secure storage
  // On web, httpOnly cookies are automatically sent with requests (credentials: 'include')
  // On mobile, we still need to get token from SecureStore and send in Authorization header
  let token: string | null = null;
  try {
    if (Platform.OS === 'web') {
      // Web: httpOnly cookies are handled automatically via credentials: 'include'
      // But we still check localStorage for backward compatibility during migration
      if (typeof window !== 'undefined' && window.localStorage) {
        token = localStorage.getItem('auth_token');
      }
    } else {
      // React Native - use SecureStore and send in Authorization header
      try {
        token = await SecureStore.getItemAsync('auth_token');
      } catch (secureStoreError) {
        // SecureStore might not be available in all environments
        logger.warn('SecureStore not available', 'api');
      }
    }
  } catch (e) {
    logger.warn('Failed to get token from storage', 'api', { error: e });
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  // Only add Authorization header for mobile (web uses httpOnly cookies)
  if (token && Platform.OS !== 'web') {
    headers['Authorization'] = `Bearer ${token}`;
  } else if (token && Platform.OS === 'web') {
    // On web, send token in header as fallback if cookie not available (migration period)
    headers['Authorization'] = `Bearer ${token}`;
  }

  logger.logRequest(options.method || 'GET', `${API_URL}${endpoint}`);
  
  // Never log token details, even in development
  if (options.body) {
    try {
      const bodyObj = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
      // Redact sensitive fields from logs
      const sanitizedBody = { ...bodyObj };
      // Redact all sensitive fields
      const sensitiveFields = [
        'password', 'currentPassword', 'newPassword', 'confirmPassword', 'oldPassword',
        'email', 'phone', 'ssn', 'tax_id'
      ];
      sensitiveFields.forEach(field => {
        if (sanitizedBody[field]) {
          sanitizedBody[field] = '[REDACTED]';
        }
      });
      logger.debug('Request body', 'api', { body: sanitizedBody });
    } catch (e) {
      logger.debug('Request body (raw)', 'api', { body: options.body });
    }
  }

  // Add timeout to prevent hanging
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

  let response: Response;
  try {
    response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      // Include credentials (cookies) with requests for httpOnly cookie support
      credentials: 'include',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timeout - server may be unavailable');
      (timeoutError as any).status = 408;
      (timeoutError as any).data = { error: 'Request timeout' };
      throw timeoutError;
    }
    throw error;
  }

  logger.logRequest(options.method || 'GET', `${API_URL}${endpoint}`, response.status);

  if (!response.ok) {
    let errorData: any;
    try {
      const text = await response.text();
      errorData = text ? JSON.parse(text) : { error: 'Request failed' };
    } catch (e) {
      errorData = { error: `Request failed with status ${response.status}` };
    }
    logger.logApiError(options.method || 'GET', endpoint, { status: response.status, data: errorData });
    // Use message if available, otherwise use error field, otherwise generic message
    // Ensure we always get a string, not an object
    const extractMessage = (val: any): string | null => {
      if (typeof val === 'string') return val;
      if (val && typeof val === 'object' && typeof val.message === 'string') return val.message;
      if (val && typeof val === 'object' && typeof val.error === 'string') return val.error;
      return null;
    };
    const errorMessage = 
      extractMessage(errorData.message) || 
      extractMessage(errorData.error) || 
      `Request failed with status ${response.status}`;
    const error = new Error(errorMessage);
    // Attach status code and full error data for handling
    (error as any).status = response.status;
    (error as any).data = errorData;
    throw error;
  }

  const data = await response.json();
  // Never log full response data with user info, even in development
  logger.debug('Response received successfully', 'api');
  return data;
}

export const authApi = {
  login: (credentials: LoginCredentials): Promise<AuthResponse> =>
    apiRequest<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  signup: (credentials: SignupCredentials): Promise<AuthResponse> =>
    apiRequest<AuthResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  getMe: (): Promise<{ user: User }> =>
    apiRequest<{ user: User }>('/api/auth/me'),

  logout: (): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>('/api/auth/logout', {
      method: 'POST',
    }),

  requestAccount: (email: string): Promise<AccountRequestResponse> =>
    apiRequest<AccountRequestResponse>('/api/auth/request-account', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  getAccountRequests: (status?: string): Promise<{ requests: AccountRequest[] }> => {
    const url = status ? `/api/auth/account-requests?status=${status}` : '/api/auth/account-requests';
    return apiRequest<{ requests: AccountRequest[] }>(url);
  },

  approveAccountRequest: (id: number, notes?: string): Promise<{ success: boolean; message: string; request: AccountRequest }> =>
    apiRequest(`/api/auth/account-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),

  denyAccountRequest: (id: number, notes?: string): Promise<{ success: boolean; message: string; request: AccountRequest }> =>
    apiRequest(`/api/auth/account-requests/${id}/deny`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    }),

  checkRequestStatus: (email: string): Promise<{ status: 'no_request' | 'pending' | 'approved' | 'denied' | 'account_exists'; message: string; requested_at?: string; reviewed_at?: string }> =>
    apiRequest(`/api/auth/check-request-status/${encodeURIComponent(email)}`),

  // Password management - old reset link methods disabled
  // forgotPassword, verifyResetToken, and resetPassword are no longer available
  // Password resets must be done by admin only

  changePassword: (data: { currentPassword: string; newPassword: string }): Promise<{ success: boolean; message: string }> =>
    apiRequest('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateProfile: (data: { display_name: string }): Promise<{ success: boolean; user: User }> =>
    apiRequest('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  getAllUsers: (): Promise<{ success: boolean; users: User[]; count: number }> =>
    apiRequest('/api/users'),

  // Admin API
  getAdminStatistics: (): Promise<{
    success: boolean;
    statistics: {
      users: any;
      accountRequests: any;
      portfolios: any;
      properties: any;
      financial: any;
      userGrowth: Array<{ month: string; count: number }>;
    };
  }> => apiRequest('/api/admin/statistics'),

  searchUsers: (params?: {
    search?: string;
    role?: string;
    email_verified?: boolean;
    has_logged_in?: boolean;
    sort_by?: string;
    sort_order?: 'ASC' | 'DESC';
    limit?: number;
    offset?: number;
  }): Promise<{
    success: boolean;
    users: User[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    };
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.email_verified !== undefined) queryParams.append('email_verified', String(params.email_verified));
    if (params?.has_logged_in !== undefined) queryParams.append('has_logged_in', String(params.has_logged_in));
    if (params?.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params?.sort_order) queryParams.append('sort_order', params.sort_order);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    const query = queryParams.toString();
    return apiRequest(`/api/admin/users/search${query ? `?${query}` : ''}`);
  },

  updateUserRole: (userId: number, role: 'admin' | 'user'): Promise<{ success: boolean; message: string; user: User }> =>
    apiRequest(`/api/admin/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  resetUserPassword: (userId: number, newPassword: string): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/admin/users/${userId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    }),

  getUserDetails: (userId: number): Promise<{
    success: boolean;
    user: User;
    portfolios: Array<{ id: number; name: string; description: string | null; created_at: string; property_count: string }>;
    propertyStats: { total_properties: string; owned_properties: string };
  }> => apiRequest(`/api/admin/users/${userId}/details`),

  bulkAccountRequestAction: (requestIds: number[], action: 'approve' | 'deny', notes?: string): Promise<{
    success: boolean;
    message: string;
    updated: number;
    requests: AccountRequest[];
  }> =>
    apiRequest('/api/admin/account-requests/bulk-action', {
      method: 'POST',
      body: JSON.stringify({ request_ids: requestIds, action, notes }),
    }),

  // Analytics Admin API
  getAnalyticsOverview: (days?: number): Promise<{
    success: boolean;
    analytics: {
      activeUsers: { dau: string; wau: string; mau: string };
      sessions: { total_sessions: string; today_sessions: string; avg_duration_ms: number; avg_page_views: number };
      events: { total_events: string; today_events: string; unique_users: string };
      topPages: Array<{ page: string; views: string; unique_visitors: string }>;
      featureUsage: Array<{ event_type: string; usage_count: string; unique_users: string }>;
      deviceBreakdown: Array<{ device_type: string; count: string; percentage: string }>;
      platformBreakdown: Array<{ platform: string; count: string; percentage: string }>;
      period: string;
    };
  }> => apiRequest(`/api/analytics/admin/overview${days ? `?days=${days}` : ''}`),

  getAnalyticsTrends: (days?: number, interval?: 'hour' | 'day' | 'week'): Promise<{
    success: boolean;
    trends: {
      activeUsers: Array<{ period: string; activeUsers: number; totalEvents: number }>;
      sessions: Array<{ period: string; sessions: number; avgDurationSeconds: number }>;
      categories: Array<{ period: string; event_category: string; count: string }>;
      period: string;
      interval: string;
    };
  }> => {
    const params = new URLSearchParams();
    if (days) params.append('days', String(days));
    if (interval) params.append('interval', interval);
    const query = params.toString();
    return apiRequest(`/api/analytics/admin/trends${query ? `?${query}` : ''}`);
  },

  getAnalyticsRealtime: (): Promise<{
    success: boolean;
    realtime: {
      activeUsers: number;
      events: number;
      recentEvents: Array<{ eventType: string; page: string; timestamp: string; user: string }>;
      activePages: Array<{ page: string; views: string }>;
      asOf: string;
    };
  }> => apiRequest('/api/analytics/admin/realtime'),

  getUserAnalytics: (userId: number): Promise<{
    success: boolean;
    userAnalytics: {
      summary: {
        total_events: string;
        active_days: string;
        first_activity: string;
        last_activity: string;
        total_sessions: string;
        avg_duration_ms: number;
        total_page_views: string;
      };
      topFeatures: Array<{ event_type: string; usage_count: string }>;
      recentActivity: Array<{ event_type: string; page: string; created_at: string }>;
    };
  }> => apiRequest(`/api/analytics/admin/user/${userId}`),

  // Portfolio API
  createPortfolio: (data: CreatePortfolioRequest): Promise<CreatePortfolioResponse> =>
    apiRequest<CreatePortfolioResponse>('/api/portfolios', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getPortfolios: (): Promise<PortfolioListResponse> =>
    apiRequest<PortfolioListResponse>('/api/portfolios'),

  getPortfolio: (id: number): Promise<PortfolioResponse> =>
    apiRequest<PortfolioResponse>(`/api/portfolios/${id}`),

  updatePortfolio: (id: number, data: UpdatePortfolioRequest): Promise<CreatePortfolioResponse> =>
    apiRequest<CreatePortfolioResponse>(`/api/portfolios/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deletePortfolio: (id: number): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/portfolios/${id}`, {
      method: 'DELETE',
    }),

  // Portfolio Partner API (ownership is property-level, not portfolio-level)
  invitePartner: (portfolioId: number, data: InvitePartnerRequest): Promise<InvitePartnerResponse> =>
    apiRequest<InvitePartnerResponse>(`/api/portfolios/${portfolioId}/invite`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removePartner: (portfolioId: number, userId: number): Promise<RemovePartnerResponse> =>
    apiRequest<RemovePartnerResponse>(`/api/portfolios/${portfolioId}/partners/${userId}`, {
      method: 'DELETE',
    }),

  // Property API
  createProperty: (portfolioId: number, data: CreatePropertyRequest): Promise<CreatePropertyResponse> =>
    apiRequest<CreatePropertyResponse>(`/api/portfolios/${portfolioId}/properties`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getProperties: (portfolioId: number, view?: PortfolioView, status?: string): Promise<PropertyListResponse> => {
    const params = new URLSearchParams();
    if (view) params.append('view', view);
    if (status) params.append('status', status);
    const query = params.toString();
    return apiRequest<PropertyListResponse>(`/api/portfolios/${portfolioId}/properties${query ? `?${query}` : ''}`);
  },

  getProperty: (portfolioId: number, propertyId: number): Promise<PropertyResponse> =>
    apiRequest<PropertyResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}`),

  updateProperty: (portfolioId: number, propertyId: number, data: UpdatePropertyRequest): Promise<PropertyResponse> =>
    apiRequest<PropertyResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteProperty: (portfolioId: number, propertyId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/portfolios/${portfolioId}/properties/${propertyId}`, {
      method: 'DELETE',
    }),

  // Geocoding API
  geocodeProperty: (portfolioId: number, propertyId: number): Promise<GeocodePropertyResponse> =>
    apiRequest<GeocodePropertyResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/geocode`, {
      method: 'POST',
    }),

  searchAddresses: (query: string, options?: { limit?: number; proximity?: [number, number] }): Promise<AddressSearchResponse> =>
    apiRequest<AddressSearchResponse>('/api/portfolios/geocode/search', {
      method: 'POST',
      body: JSON.stringify({ query, ...options }),
    }),

  // Property Partner API (property-level ownership)
  addPropertyPartner: (portfolioId: number, propertyId: number, data: AddPropertyPartnerRequest): Promise<PropertyPartnerResponse> =>
    apiRequest<PropertyPartnerResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/partners`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePropertyPartnerOwnership: (portfolioId: number, propertyId: number, userId: number, data: UpdatePropertyPartnerRequest): Promise<PropertyPartnerResponse> =>
    apiRequest<PropertyPartnerResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/partners/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  removePropertyPartner: (portfolioId: number, propertyId: number, userId: number): Promise<PropertyPartnerResponse> =>
    apiRequest<PropertyPartnerResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/partners/${userId}`, {
      method: 'DELETE',
    }),

  // ==========================================================================
  // Opportunity API (NEW - replaces Property API)
  // ==========================================================================

  createOpportunity: (portfolioId: number, data: CreateOpportunityRequest): Promise<CreateOpportunityResponse> =>
    apiRequest<CreateOpportunityResponse>(`/api/portfolios/${portfolioId}/opportunities`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getOpportunities: (portfolioId: number, view?: OpportunityView, status?: OpportunityStatus): Promise<OpportunityListResponse> => {
    const params = new URLSearchParams();
    if (view) params.append('view', view);
    if (status) params.append('status', status);
    const query = params.toString();
    return apiRequest<OpportunityListResponse>(`/api/portfolios/${portfolioId}/opportunities${query ? `?${query}` : ''}`);
  },

  getOpportunity: (portfolioId: number, opportunityId: number): Promise<OpportunityResponse> =>
    apiRequest<OpportunityResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}`),

  updateOpportunity: (portfolioId: number, opportunityId: number, data: UpdateOpportunityRequest): Promise<OpportunityResponse> =>
    apiRequest<OpportunityResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteOpportunity: (portfolioId: number, opportunityId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}`, {
      method: 'DELETE',
    }),

  passOpportunity: (portfolioId: number, opportunityId: number, reason?: string): Promise<OpportunityResponse> =>
    apiRequest<OpportunityResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/pass`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  // Activity log for opportunity history
  getOpportunityActivity: (opportunityId: number, limit = 50, offset = 0): Promise<ActivityLogResponse> =>
    apiRequest<ActivityLogResponse>(`/api/opportunities/${opportunityId}/activity?limit=${limit}&offset=${offset}`),

  // Opportunity Photos API
  getOpportunityPhotos: (portfolioId: number, opportunityId: number): Promise<{ success: boolean; photos: OpportunityPhoto[]; count: number }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/photos`),

  getOpportunityPhoto: (portfolioId: number, opportunityId: number, photoId: number): Promise<{ success: boolean; photo: OpportunityPhoto & { image_data: string } }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/photos/${photoId}`),

  uploadOpportunityPhoto: (portfolioId: number, opportunityId: number, data: {
    filename: string; mime_type?: string; image_data: string; thumbnail_data?: string;
    caption?: string; latitude?: number | null; longitude?: number | null; taken_at?: string;
  }): Promise<{ success: boolean; photo: OpportunityPhoto }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/photos`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteOpportunityPhoto: (portfolioId: number, opportunityId: number, photoId: number): Promise<{ success: boolean }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/photos/${photoId}`, {
      method: 'DELETE',
    }),

  // Voice Notes API
  getVoiceNotes: (portfolioId: number, opportunityId: number): Promise<{ success: boolean; voiceNotes: VoiceNote[]; count: number }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/voice-notes`),

  getVoiceNote: (portfolioId: number, opportunityId: number, noteId: number): Promise<{ success: boolean; voiceNote: VoiceNote & { audio_data: string } }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/voice-notes/${noteId}`),

  uploadVoiceNote: (portfolioId: number, opportunityId: number, data: {
    audio_data: string;
    filename: string;
    mime_type?: string;
    duration_ms?: number;
    caption?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<{ success: boolean; voiceNote: VoiceNote }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/voice-notes`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deleteVoiceNote: (portfolioId: number, opportunityId: number, noteId: number): Promise<{ success: boolean }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/voice-notes/${noteId}`, {
      method: 'DELETE',
    }),

  // Field Notes API
  getFieldNotes: (portfolioId: number, opportunityId: number): Promise<{ success: boolean; fieldNotes: FieldNote[]; count: number }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/field-notes`),

  createFieldNote: (portfolioId: number, opportunityId: number, data: {
    category?: FieldNoteCategory; title?: string; body: string;
    latitude?: number | null; longitude?: number | null;
  }): Promise<{ success: boolean; fieldNote: FieldNote }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/field-notes`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateFieldNote: (portfolioId: number, opportunityId: number, noteId: number, data: {
    category?: FieldNoteCategory; title?: string; body?: string;
  }): Promise<{ success: boolean; fieldNote: FieldNote }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/field-notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteFieldNote: (portfolioId: number, opportunityId: number, noteId: number): Promise<{ success: boolean }> =>
    apiRequest(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/field-notes/${noteId}`, {
      method: 'DELETE',
    }),

  // Parcel management
  unlinkParcelFromOpportunity: (opportunityId: number, featureId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/parcels/property/${opportunityId}/features/${featureId}`, {
      method: 'DELETE',
    }),

  // Parcel tools (merge, split)
  mergeParcels: (opportunityId: number, featureIds: number[]): Promise<{
    success: boolean; feature: { id: number; geometry: object; area_acres: number | null; parcel_id: string };
  }> =>
    apiRequest('/api/parcel-tools/merge', {
      method: 'POST',
      body: JSON.stringify({ opportunityId, featureIds }),
    }),

  splitParcel: (featureId: number, splitLine: object): Promise<{
    success: boolean; parts: Array<{ index: number; geometry: object; area_acres: number }>;
    originalFeatureId: number;
  }> =>
    apiRequest('/api/parcel-tools/split', {
      method: 'POST',
      body: JSON.stringify({ featureId, splitLine }),
    }),

  confirmSplit: (opportunityId: number, originalFeatureId: number, geometry: object, area_acres: number): Promise<{
    success: boolean; feature: { id: number; geometry: object; area_acres: number };
  }> =>
    apiRequest('/api/parcel-tools/split/confirm', {
      method: 'POST',
      body: JSON.stringify({ opportunityId, originalFeatureId, geometry, area_acres }),
    }),

  recomputeParcelArea: (featureId: number, opportunityId?: number): Promise<{ success: boolean; area_acres: number }> =>
    apiRequest('/api/parcel-tools/recompute-area', {
      method: 'POST',
      body: JSON.stringify({ featureId, opportunityId }),
    }),

  // Opportunity Partner API
  addOpportunityPartner: (portfolioId: number, opportunityId: number, data: AddOpportunityPartnerRequest): Promise<OpportunityPartnerResponse> =>
    apiRequest<OpportunityPartnerResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/partners`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateOpportunityPartner: (portfolioId: number, opportunityId: number, userId: number, data: UpdateOpportunityPartnerRequest): Promise<OpportunityPartnerResponse> =>
    apiRequest<OpportunityPartnerResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/partners/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  removeOpportunityPartner: (portfolioId: number, opportunityId: number, userId: number): Promise<OpportunityPartnerResponse> =>
    apiRequest<OpportunityPartnerResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/partners/${userId}`, {
      method: 'DELETE',
    }),

  // Financial Entry API
  createFinancialEntry: (portfolioId: number, propertyId: number, data: CreateFinancialEntryRequest): Promise<FinancialEntryResponse> =>
    apiRequest<FinancialEntryResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/financial-entries`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getFinancialEntries: (portfolioId: number, propertyId: number, filters?: { type?: string; start_date?: string; end_date?: string }): Promise<FinancialEntryListResponse> => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    const query = params.toString();
    return apiRequest<FinancialEntryListResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/financial-entries${query ? `?${query}` : ''}`);
  },

  updateFinancialEntry: (portfolioId: number, propertyId: number, entryId: number, data: UpdateFinancialEntryRequest): Promise<FinancialEntryResponse> =>
    apiRequest<FinancialEntryResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/financial-entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteFinancialEntry: (portfolioId: number, propertyId: number, entryId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/portfolios/${portfolioId}/properties/${propertyId}/financial-entries/${entryId}`, {
      method: 'DELETE',
    }),

  getPortfolioFinancialSummary: (portfolioId: number, filters?: { start_date?: string; end_date?: string }): Promise<PortfolioFinancialSummaryResponse> => {
    const params = new URLSearchParams();
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    const query = params.toString();
    return apiRequest<PortfolioFinancialSummaryResponse>(`/api/portfolios/${portfolioId}/financial-summary${query ? `?${query}` : ''}`);
  },

  // Mortgage Calculator API
  createMortgageCalculation: (portfolioId: number, propertyId: number, data: CreateMortgageCalculationRequest): Promise<MortgageCalculationResponse> =>
    apiRequest<MortgageCalculationResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/mortgage-calculations`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getMortgageCalculations: (portfolioId: number, propertyId: number): Promise<MortgageCalculationListResponse> =>
    apiRequest<MortgageCalculationListResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/mortgage-calculations`, {
      method: 'GET',
    }),

  getMortgageCalculation: (portfolioId: number, propertyId: number, calculationId: number): Promise<MortgageCalculationResponse> =>
    apiRequest<MortgageCalculationResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/mortgage-calculations/${calculationId}`, {
      method: 'GET',
    }),

  deleteMortgageCalculation: (portfolioId: number, propertyId: number, calculationId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/portfolios/${portfolioId}/properties/${propertyId}/mortgage-calculations/${calculationId}`, {
      method: 'DELETE',
    }),

  // Due Diligence Checklist API
  getDueDiligenceChecklist: (portfolioId: number, propertyId: number): Promise<ChecklistResponse> =>
    apiRequest<ChecklistResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/due-diligence`),

  createChecklistItem: (portfolioId: number, propertyId: number, data: CreateChecklistItemRequest): Promise<ChecklistItemResponse> =>
    apiRequest<ChecklistItemResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/due-diligence/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateChecklistItem: (portfolioId: number, propertyId: number, itemId: number, data: UpdateChecklistItemRequest): Promise<ChecklistItemResponse> =>
    apiRequest<ChecklistItemResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/due-diligence/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteChecklistItem: (portfolioId: number, propertyId: number, itemId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/portfolios/${portfolioId}/properties/${propertyId}/due-diligence/items/${itemId}`, {
      method: 'DELETE',
    }),

  saveItemNote: (portfolioId: number, propertyId: number, itemId: number, data: CreateItemNoteRequest): Promise<ItemNoteResponse> =>
    apiRequest<ItemNoteResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/due-diligence/items/${itemId}/notes`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Property Potential Survey API
  getPropertyPotentialSurveys: (portfolioId: number, propertyId: number): Promise<SurveyListResponse> =>
    apiRequest<SurveyListResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/property-potential-surveys`),

  createPropertyPotentialSurvey: (portfolioId: number, propertyId: number, data?: CreateSurveyRequest): Promise<SurveyResponse> =>
    apiRequest<SurveyResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/property-potential-surveys`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }),

  createSurveyItem: (portfolioId: number, propertyId: number, surveyId: number, data: CreateSurveyItemRequest): Promise<SurveyItemResponse> =>
    apiRequest<SurveyItemResponse>(`/api/portfolios/${portfolioId}/properties/${propertyId}/property-potential-surveys/${surveyId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  toggleSurveyItemVote: (portfolioId: number, propertyId: number, surveyId: number, itemId: number): Promise<{ success: boolean; action: string; vote_count: number; user_has_voted: boolean; votes: PropertyPotentialSurveyVote[] }> =>
    apiRequest(`/api/portfolios/${portfolioId}/properties/${propertyId}/property-potential-surveys/${surveyId}/items/${itemId}/vote`, {
      method: 'POST',
    }),

  deleteSurveyItem: (portfolioId: number, propertyId: number, surveyId: number, itemId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/portfolios/${portfolioId}/properties/${propertyId}/property-potential-surveys/${surveyId}/items/${itemId}`, {
      method: 'DELETE',
    }),

  // Comment API
  createCommentThread: (data: CreateCommentThreadRequest): Promise<CommentThreadResponse> =>
    apiRequest<CommentThreadResponse>('/api/comments/threads', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getCommentThreads: (contextType: string, contextId: number): Promise<CommentThreadListResponse> => {
    const params = new URLSearchParams();
    params.append('context_type', contextType);
    params.append('context_id', String(contextId));
    return apiRequest<CommentThreadListResponse>(`/api/comments/threads?${params.toString()}`);
  },

  getCommentThread: (threadId: number): Promise<{ success: boolean; thread: CommentThread; comments: Comment[]; count: number }> =>
    apiRequest(`/api/comments/threads/${threadId}`),

  createComment: (data: CreateCommentRequest): Promise<CommentResponse> =>
    apiRequest<CommentResponse>('/api/comments', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateComment: (commentId: number, data: UpdateCommentRequest): Promise<CommentResponse> =>
    apiRequest<CommentResponse>(`/api/comments/${commentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteComment: (commentId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/comments/${commentId}`, {
      method: 'DELETE',
    }),

  addCommentReaction: (commentId: number, data: AddCommentReactionRequest): Promise<CommentReactionResponse> =>
    apiRequest<CommentReactionResponse>(`/api/comments/${commentId}/reactions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  removeCommentReaction: (commentId: number, reactionType: string): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/comments/${commentId}/reactions`, {
      method: 'DELETE',
      body: JSON.stringify({ reaction_type: reactionType }),
    }),

  markThreadRead: (threadId: number): Promise<MarkThreadReadResponse> =>
    apiRequest<MarkThreadReadResponse>(`/api/comments/threads/${threadId}/read`, {
      method: 'POST',
    }),

  getAllThreads: (filters?: { context_type?: string; limit?: number; offset?: number }): Promise<CommentThreadListResponse> => {
    const params = new URLSearchParams();
    if (filters?.context_type) params.append('context_type', filters.context_type);
    if (filters?.limit) params.append('limit', String(filters.limit));
    if (filters?.offset) params.append('offset', String(filters.offset));
    const query = params.toString();
    return apiRequest<CommentThreadListResponse>(`/api/comments/threads-list${query ? `?${query}` : ''}`);
  },

  // Contact Types API
  getContactTypes: (): Promise<ContactTypesResponse> =>
    apiRequest<ContactTypesResponse>('/api/contacts/types'),

  // User-level Contacts API
  getUserContacts: (params?: { type?: string; search?: string }): Promise<ContactListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return apiRequest<ContactListResponse>(`/api/contacts${query ? `?${query}` : ''}`);
  },

  getUserContact: (contactId: number): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/contacts/${contactId}`),

  createUserContact: (data: CreateContactRequest): Promise<ContactResponse> =>
    apiRequest<ContactResponse>('/api/contacts', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUserContact: (contactId: number, data: UpdateContactRequest): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteUserContact: (contactId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/contacts/${contactId}`, {
      method: 'DELETE',
    }),

  // Portfolio-level Contacts API
  getPortfolioContacts: (portfolioId: number, params?: { type?: string; search?: string }): Promise<ContactListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return apiRequest<ContactListResponse>(`/api/portfolios/${portfolioId}/contacts${query ? `?${query}` : ''}`);
  },

  getPortfolioContact: (portfolioId: number, contactId: number): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/portfolios/${portfolioId}/contacts/${contactId}`),

  createPortfolioContact: (portfolioId: number, data: CreateContactRequest): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/portfolios/${portfolioId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePortfolioContact: (portfolioId: number, contactId: number, data: UpdateContactRequest): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/portfolios/${portfolioId}/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deletePortfolioContact: (portfolioId: number, contactId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/portfolios/${portfolioId}/contacts/${contactId}`, {
      method: 'DELETE',
    }),

  // Property-level Contacts API
  getPropertyContacts: (portfolioId: number, opportunityId: number, params?: { type?: string; search?: string }): Promise<ContactListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return apiRequest<ContactListResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/contacts${query ? `?${query}` : ''}`);
  },

  getPropertyContact: (portfolioId: number, opportunityId: number, contactId: number): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/contacts/${contactId}`),

  createPropertyContact: (portfolioId: number, opportunityId: number, data: CreateContactRequest): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updatePropertyContact: (portfolioId: number, opportunityId: number, contactId: number, data: UpdateContactRequest): Promise<ContactResponse> =>
    apiRequest<ContactResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deletePropertyContact: (portfolioId: number, opportunityId: number, contactId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest<{ success: boolean; message: string }>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/contacts/${contactId}`, {
      method: 'DELETE',
    }),

  // Feedback API
  createFeedback: (data: CreateFeedbackRequest): Promise<FeedbackResponse> =>
    apiRequest<FeedbackResponse>('/api/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getFeedback: (feedbackId: number): Promise<FeedbackResponse> =>
    apiRequest<FeedbackResponse>(`/api/feedback/${feedbackId}`),

  getFeedbackList: (params?: {
    type?: string;
    status?: string;
    priority?: string;
    limit?: number;
    offset?: number;
  }): Promise<FeedbackListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    const query = queryParams.toString();
    return apiRequest<FeedbackListResponse>(`/api/feedback${query ? `?${query}` : ''}`);
  },

  getFeedbackStats: (): Promise<FeedbackStatsResponse> =>
    apiRequest<FeedbackStatsResponse>('/api/feedback/stats'),

  updateFeedback: (
    feedbackId: number, 
    updates: { status?: string; priority?: string; admin_notes?: string }
  ): Promise<FeedbackResponse> =>
    apiRequest<FeedbackResponse>(`/api/feedback/${feedbackId}/status`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  // Due Diligence V2 API
  getDueDiligence: (portfolioId: number, opportunityId: number): Promise<DueDiligenceResponse> =>
    apiRequest<DueDiligenceResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/due-diligence`),

  createOrUpdateDueDiligence: (
    portfolioId: number,
    opportunityId: number,
    data: CreateDueDiligenceRequest | UpdateDueDiligenceRequest
  ): Promise<DueDiligenceResponse> =>
    apiRequest<DueDiligenceResponse>(`/api/portfolios/${portfolioId}/opportunities/${opportunityId}/due-diligence`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  initializeDueDiligence: (
    portfolioId: number,
    opportunityId: number,
    data: { basePropertyType: string; enabledModules: string[]; items: any[] }
  ): Promise<{ success: boolean; message: string; itemsCreated: number }> =>
    apiRequest<{ success: boolean; message: string; itemsCreated: number }>(
      `/api/portfolios/${portfolioId}/opportunities/${opportunityId}/due-diligence/initialize`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    ),

  updateDueDiligenceItem: (
    portfolioId: number,
    opportunityId: number,
    itemId: number,
    data: UpdateDueDiligenceItemRequest
  ): Promise<{ success: boolean; item: DueDiligenceItem }> =>
    apiRequest<{ success: boolean; item: DueDiligenceItem }>(
      `/api/portfolios/${portfolioId}/opportunities/${opportunityId}/due-diligence/items/${itemId}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    ),

  // ==========================================================================
  // Data Lookup (Geocoding, Property Data Enrichment)
  // ==========================================================================

  /**
   * Geocode an address to get coordinates and structured address data
   */
  geocodeAddress: (
    address: string
  ): Promise<GeocodeResult> =>
    apiRequest<GeocodeResult>('/api/data-lookup/geocode', {
      method: 'POST',
      body: JSON.stringify({ address }),
    }),

  /**
   * Reverse geocode coordinates to get address
   */
  reverseGeocode: (
    latitude: number,
    longitude: number
  ): Promise<GeocodeResult> =>
    apiRequest<GeocodeResult>('/api/data-lookup/reverse-geocode', {
      method: 'POST',
      body: JSON.stringify({ latitude, longitude }),
    }),

  /**
   * Validate and standardize an address
   */
  validateAddress: (
    address: string
  ): Promise<AddressValidationResult> =>
    apiRequest<AddressValidationResult>('/api/data-lookup/validate-address', {
      method: 'POST',
      body: JSON.stringify({ address }),
    }),

  /**
   * Comprehensive property data lookup
   * Combines geocoding with other data sources
   */
  propertyLookup: (
    params: { address?: string; latitude?: number; longitude?: number }
  ): Promise<PropertyLookupResult> =>
    apiRequest<PropertyLookupResult>('/api/data-lookup/property-lookup', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // ==========================================================================
  // Smart Contacts (Partnership-Amplified Contact Intelligence)
  // ==========================================================================

  /**
   * Get all contacts relevant to a property from all partners
   * Includes: property-level, portfolio-level, and user-level contacts from all partners
   */
  getSmartContacts: (
    portfolioId: number,
    propertyId: number,
    params?: { type?: string; category?: string }
  ): Promise<SmartContactsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.type) queryParams.append('type', params.type);
    if (params?.category) queryParams.append('category', params.category);
    const queryString = queryParams.toString();
    return apiRequest<SmartContactsResponse>(
      `/api/portfolios/${portfolioId}/properties/${propertyId}/smart-contacts${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get suggested contact types for a due diligence category
   */
  getSuggestedContactTypes: (
    portfolioId: number,
    propertyId: number,
    category: string
  ): Promise<{ success: boolean; category: string; suggested_types: string[] }> =>
    apiRequest<{ success: boolean; category: string; suggested_types: string[] }>(
      `/api/portfolios/${portfolioId}/properties/${propertyId}/smart-contacts/suggest?category=${category}`
    ),

  // ==========================================================================
  // Parcel / Geographic Feature Operations
  // ==========================================================================

  /**
   * Get all available layer types
   */
  getLayerTypes: (): Promise<LayerTypesResponse> =>
    apiRequest<LayerTypesResponse>('/api/parcels/layers'),

  /**
   * Get all available data sources
   */
  getParcelDataSources: (): Promise<DataSourcesResponse> =>
    apiRequest<DataSourcesResponse>('/api/parcels/sources'),

  /**
   * Test NC OneMap connection
   */
  testParcelConnection: (): Promise<{ success: boolean; serviceInfo?: any; error?: string }> =>
    apiRequest<{ success: boolean; serviceInfo?: any; error?: string }>('/api/parcels/sources/test'),

  /**
   * Look up parcel by coordinates, address, or parcel ID
   */
  lookupParcel: (
    params: ParcelLookupParams
  ): Promise<ParcelLookupResponse> => {
    const queryParams = new URLSearchParams();
    if (params.lat !== undefined) queryParams.append('lat', String(params.lat));
    if (params.lng !== undefined) queryParams.append('lng', String(params.lng));
    if (params.address) queryParams.append('address', params.address);
    if (params.parcel_id) queryParams.append('parcel_id', params.parcel_id);
    if (params.state) queryParams.append('state', params.state);
    if (params.county) queryParams.append('county', params.county);
    if (params.include_neighbors !== undefined) queryParams.append('include_neighbors', String(params.include_neighbors));
    if (params.max_tier !== undefined) queryParams.append('max_tier', String(params.max_tier));
    if (params.include_geometry !== undefined) queryParams.append('include_geometry', String(params.include_geometry));
    return apiRequest<ParcelLookupResponse>(`/api/parcels/lookup?${queryParams.toString()}`);
  },

  /**
   * Get a specific feature by ID
   */
  getFeature: (featureId: number): Promise<FeatureResponse> =>
    apiRequest<FeatureResponse>(`/api/parcels/features/${featureId}`),

  /**
   * Get neighbors for a feature
   */
  getFeatureNeighbors: (
    featureId: number,
    maxTier: number = 3
  ): Promise<NeighborsResponse> =>
    apiRequest<NeighborsResponse>(`/api/parcels/features/${featureId}/neighbors?max_tier=${maxTier}`),

  /**
   * Get all features linked to a property
   */
  getPropertyFeatures: (
    propertyId: number,
    params?: { include_neighbors?: boolean; max_tier?: number }
  ): Promise<PropertyFeaturesResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.include_neighbors !== undefined) queryParams.append('include_neighbors', String(params.include_neighbors));
    if (params?.max_tier !== undefined) queryParams.append('max_tier', String(params.max_tier));
    const queryString = queryParams.toString();
    return apiRequest<PropertyFeaturesResponse>(
      `/api/parcels/property/${propertyId}${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Link a property to a parcel (manual or auto)
   */
  linkPropertyToParcel: (
    propertyId: number,
    params: LinkParcelParams
  ): Promise<LinkParcelResponse> =>
    apiRequest<LinkParcelResponse>(`/api/parcels/property/${propertyId}/link`, {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  /**
   * Refresh parcel data for a property from external source
   */
  refreshPropertyParcels: (
    propertyId: number,
    params?: { include_neighbors?: boolean; max_tier?: number }
  ): Promise<LinkParcelResponse> =>
    apiRequest<LinkParcelResponse>(`/api/parcels/property/${propertyId}/refresh`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    }),

  /**
   * Search parcels by owner or address
   */
  searchParcels: (
    params: ParcelSearchParams
  ): Promise<ParcelSearchResponse> => {
    const queryParams = new URLSearchParams();
    if (params.owner) queryParams.append('owner', params.owner);
    if (params.address) queryParams.append('address', params.address);
    if (params.county) queryParams.append('county', params.county);
    if (params.state) queryParams.append('state', params.state);
    if (params.min_acres !== undefined) queryParams.append('min_acres', String(params.min_acres));
    if (params.max_acres !== undefined) queryParams.append('max_acres', String(params.max_acres));
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    return apiRequest<ParcelSearchResponse>(`/api/parcels/search?${queryParams.toString()}`);
  },

  // ===========================================================================
  // IDEAS API
  // ===========================================================================

  /**
   * List ideas for an opportunity
   */
  getIdeas: (opportunityId: number, filters?: IdeaFilters): Promise<IdeaListResponse> => {
    const queryParams = new URLSearchParams();
    if (filters?.category && filters.category !== 'all') queryParams.append('category', filters.category);
    if (filters?.status && filters.status !== 'all') queryParams.append('status', filters.status);
    if (filters?.priority && filters.priority !== 'all') queryParams.append('priority', filters.priority);
    if (filters?.sort_by) queryParams.append('sort_by', filters.sort_by);
    const queryString = queryParams.toString();
    return apiRequest<IdeaListResponse>(
      `/api/opportunities/${opportunityId}/ideas${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Create a new idea
   */
  createIdea: (opportunityId: number, data: CreateIdeaRequest): Promise<IdeaResponse> =>
    apiRequest<IdeaResponse>(`/api/opportunities/${opportunityId}/ideas`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get a single idea
   */
  getIdea: (ideaId: number): Promise<IdeaResponse> =>
    apiRequest<IdeaResponse>(`/api/ideas/${ideaId}`),

  /**
   * Update an idea
   */
  updateIdea: (ideaId: number, data: UpdateIdeaRequest): Promise<IdeaResponse> =>
    apiRequest<IdeaResponse>(`/api/ideas/${ideaId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Delete an idea
   */
  deleteIdea: (ideaId: number): Promise<{ success: boolean; message: string }> =>
    apiRequest(`/api/ideas/${ideaId}`, { method: 'DELETE' }),

  /**
   * Vote on an idea (or remove vote with vote_type: null)
   */
  voteIdea: (ideaId: number, data: VoteIdeaRequest | { vote_type: null }): Promise<IdeaVoteResponse> =>
    apiRequest<IdeaVoteResponse>(`/api/ideas/${ideaId}/vote`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * List comments for an idea
   */
  getIdeaComments: (ideaId: number): Promise<IdeaCommentListResponse> =>
    apiRequest<IdeaCommentListResponse>(`/api/ideas/${ideaId}/comments`),

  /**
   * Add a comment to an idea
   */
  addIdeaComment: (ideaId: number, data: CreateIdeaCommentRequest): Promise<IdeaCommentResponse> =>
    apiRequest<IdeaCommentResponse>(`/api/ideas/${ideaId}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get activity log for an idea
   */
  getIdeaActivity: (ideaId: number): Promise<IdeaActivityListResponse> =>
    apiRequest<IdeaActivityListResponse>(`/api/ideas/${ideaId}/activity`),
};

